'use client';

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchJunkdrawerDocumentPageBlob,
  junkdrawerKeys,
  type JunkdrawerBlobResponse,
  type JunkdrawerDocumentApiPath,
} from '@/shared/cors-junkdrawer-api';
import { useCellServiceSession } from './useCellServiceSession';

type UseJunkdrawerDocumentPageOptions = {
  /** Source S3 URL (passed through to the upstream as ``url``). */
  s3Url: string;
  /** 1-based page number. */
  page: number;
  /** ``"pdf"`` for native PDFs, ``"ms-word"`` for DOCX → PDF rendering. */
  apiPath: JunkdrawerDocumentApiPath;
  /** Optional gate for the caller (in addition to the implicit session check). */
  enabled?: boolean;
};

/**
 * Delay before revoking a stale object URL after a new blob takes its place.
 * pdf.js's async worker pipeline (document parsing, page render dispatch)
 * may still be reading from the URL when the blob changes. Revoking
 * synchronously in the effect cleanup races with those in-flight reads and
 * surfaces "Cannot fetch from revoked object URL" errors that activate the
 * viewer's error boundary. The grace period is generous enough to cover
 * worker drains on slow devices without holding blob memory indefinitely.
 */
const REVOKE_GRACE_MS = 30_000;

/** ``junkdrawerKeys.documentPage`` shape: ``[..., apiPath, cellId, s3Url, page]``. */
function isSameJunkdrawerDocumentQueryKey(
  previousKey: readonly unknown[] | undefined,
  nextKey: readonly unknown[],
): boolean {
  if (!previousKey || previousKey.length < 6) {
    return false;
  }
  return (
    previousKey[2] === nextKey[2] &&
    previousKey[3] === nextKey[3] &&
    previousKey[4] === nextKey[4]
  );
}

export type UseJunkdrawerDocumentPageResult = {
  /**
   * ``URL.createObjectURL(blob)`` for the current page, or ``null`` while
   * the *first* page hasn't been loaded yet. While the user pages through
   * a document the URL holds steady on the previous page's blob until the
   * next page's blob arrives — driven by same-document ``placeholderData``
   * on the query — so ``<Document>`` doesn't unmount
   * mid-transition. Lifecycle is managed here — the URL is revoked on
   * unmount and whenever a new blob replaces the previous.
   */
  objectUrl: string | null;
  /**
   * Total page count for the underlying document (from the
   * ``x-page-count`` response header). ``null`` when the header isn't
   * exposed by CORS or the request hasn't completed yet.
   */
  pageCount: number | null;
  /** ``true`` only while the *first* page is loading (no placeholder yet). */
  isLoading: boolean;
  /**
   * ``true`` whenever a request is in flight, including background
   * refetches that occur while the previous page's blob is still being
   * displayed via ``placeholderData``. Use this (not ``isLoading``) to
   * drive a subtle "loading next page" affordance.
   */
  isFetching: boolean;
  /**
   * ``true`` when ``objectUrl`` / ``pageCount`` reflect the *previous*
   * page while a refetch for the requested page is still in flight.
   */
  isPlaceholderData: boolean;
  error: Error | null;
  /** Re-run the junkdrawer blob fetch (e.g. after the viewer Retry control). */
  refetch: () => void;
};

/**
 * Authenticated fetch for one rendered page of a junkdrawer document.
 *
 * Fetches the blob via TanStack Query, exposes a stable object URL the
 * viewer can pass to ``<Document file={...}>``, and surfaces the upstream
 * ``x-page-count`` header so pagination UI is driven straight from the
 * cell gateway response.
 */
export function useJunkdrawerDocumentPage(
  opts: UseJunkdrawerDocumentPageOptions,
): UseJunkdrawerDocumentPageResult {
  const { s3Url, page, apiPath, enabled: callerEnabled = true } = opts;
  const { cellId, accessToken, enabled: sessionEnabled } = useCellServiceSession();

  const enabled = sessionEnabled && callerEnabled && !!s3Url;

  const queryKey = junkdrawerKeys.documentPage(cellId, s3Url, apiPath, page);

  const query = useQuery<JunkdrawerBlobResponse, Error>({
    queryKey,
    queryFn: ({ signal }) =>
      fetchJunkdrawerDocumentPageBlob({
        cellId: cellId as string,
        accessToken: accessToken as string,
        s3Url,
        page,
        apiPath,
        signal,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    // Timeout + single retry are handled in ``cors-junkdrawer-api``; avoid
    // stacking react-query retries on top of that.
    retry: false,
    // Hold the previous *page* blob in ``data`` while the next page of the
    // same document fetches. Without this, ``page`` flipping invalidates the
    // cache key and ``data`` goes ``undefined`` mid-turn — which would null
    // out ``objectUrl``, feed ``file=""`` to ``<Document>``, and force a full
    // unmount/remount because the viewer keys ``<Document>`` on ``fileUrl``.
    // Do not reuse ``keepPreviousData`` across ``s3Url`` / ``apiPath``
    // changes: that would leave the old blob in ``data`` with the same
    // reference, so the ``[query.data?.blob]`` effect would not re-run and
    // the viewer would briefly render the previous document.
    placeholderData: (previousData, previousQuery) => {
      if (
        previousData &&
        previousQuery &&
        isSameJunkdrawerDocumentQueryKey(previousQuery.queryKey, queryKey)
      ) {
        return previousData;
      }
      return undefined;
    },
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    const blob = query.data?.blob;
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => {
      // See REVOKE_GRACE_MS — defer revocation so pdf.js's async worker
      // pipeline can drain reads against this URL before it is invalidated.
      setTimeout(() => URL.revokeObjectURL(url), REVOKE_GRACE_MS);
    };
  }, [query.data?.blob]);

  return {
    objectUrl,
    pageCount: query.data?.pageCount ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: (query.error as Error | null) ?? null,
    refetch: () => {
      void query.refetch();
    },
  };
}
